import { Dayjs } from '@dw/dayjs';
import { IBlobFileMetadata } from '@dw/models/shared';

/**
 * A voice recording carried by a note or a reply — everything the waveform player needs BEFORE it
 * fetches any audio: what to draw, how wide to draw it, and the key to redeem for a playable URL.
 *
 * Carries no URL on purpose. Download links are short-lived presigned GETs, so minting one per voice
 * note on every list response would cost a round trip for audio nobody may play, and could hand
 * playback an already-expired link. {@link key} is redeemed against the note's download-link
 * endpoint on first play instead.
 */
export interface IVoiceNote {
  /** The blob key, redeemed against the teacher-note download-link endpoint to get a playable URL. */
  key: string;
  contentType: string;
  fileName: string;
  fileSizeInBytes: number;
  /** Recording length in seconds, as measured by the recording client — it sizes the scrub bar. */
  durationSeconds: number;
  /** 0–100 amplitude peaks, one per waveform bar. Empty (never null) when the recording carried no waveform. */
  waveformPeaks: number[];
}

/**
 * A recording the client has already uploaded, described for the server: the blob key from the
 * presigned-PUT flow plus the duration and waveform computed while recording.
 *
 * Only the key is authoritative — the server re-reads the blob's real content type, size and name
 * from storage, and clamps the duration and peaks rather than trusting them, since they only draw
 * the player.
 */
export interface IVoiceNoteRequest {
  /** The blob key from the upload-link flow. Must have been uploaded as `TEACHER_NOTE_VOICE_NOTE`. */
  attachmentKey: string;
  durationSeconds: number;
  /** 0–100 per bar, at most 128 entries. Omitted entirely when no waveform was computed. */
  waveformPeaks?: number[];
}

export interface ITeacherNoteRecipient {
  userId: string;
  name: string;
  readTimestamp?: Dayjs;
}

/** Who can see a reply — a single recipient's private thread, or every recipient of the note. */
export enum TEACHER_NOTE_REPLY_VISIBILITY {
  PRIVATE = 'Private',
  EVERYONE = 'Everyone',
}

export interface ITeacherNoteReply {
  replyId: string;
  text: string;
  /** Optional — a reply may be voice-only, text-only, or both. Absent on every reply sent before voice notes shipped. */
  voiceNote?: IVoiceNote;
  /**
   * The recipient whose private thread this reply belongs to — set on `Private` replies, and
   * absent on `Everyone` broadcasts (which belong to no single thread).
   *
   * Private replies are per-recipient conversations between that recipient and the note author;
   * Owner/Admins and the author see every thread, while each recipient sees only their own. In
   * admin/sent responses `replies` contains ALL threads (group by this field, bucketing the
   * broadcasts separately); in `/my` and mark-seen responses `replies` is already filtered
   * server-side to the caller's own thread plus any broadcasts.
   */
  recipientUserId?: string;
  visibility: TEACHER_NOTE_REPLY_VISIBILITY;
  auditUserId: string;
  auditUserName: string;
  /**
   * Everyone who can see this reply, EXCLUDING its own author — a private reply reaches the note
   * author plus that one recipient, a broadcast reaches the author plus every current recipient.
   * Removed recipients are not counted. Zero means nobody else can see it any more.
   */
  audienceCount: number;
  readByCount: number;
  /**
   * Who has read it. Populated only on the author/admin representation; deliberately empty on the
   * recipient's own view, so a recipient is never handed their co-recipients' identities.
   */
  readByNames: string[];
  /**
   * The rest of the audience — everyone who can see the reply but has not read it yet. Same
   * privacy split as {@link readByNames} (empty on the recipient view), and the two together
   * account for the whole audience: `readByNames.length + pendingReadNames.length === audienceCount`.
   */
  pendingReadNames: string[];
  createdTimestamp: Dayjs;
}

/**
 * Per-thread rollup returned alongside a note — one entry per conversation the caller can see.
 * Always returned, including for 1:1 notes (a single entry).
 */
export interface ITeacherNoteThreadSummary {
  /** The thread owner. Absent on the shared `Everyone` thread, which belongs to no one recipient. */
  recipientUserId?: string;
  /** The thread owner's display name. Absent on the `Everyone` thread. */
  name?: string;
  replyCount: number;
  /** Replies in this thread the caller has not seen. */
  unreadReplyCount: number;
  lastActivityTimestamp: Dayjs;
}

export interface ITeacherNote {
  noteId: string;
  danceSchoolId: string;
  text: string;
  attachments: IBlobFileMetadata[];
  /** Optional — a note may be voice-only, text-only, or both. Absent on every note sent before voice notes shipped. */
  voiceNote?: IVoiceNote;
  noteDate: Dayjs;
  auditUserId: string;
  auditUserName: string;
  recipients: ITeacherNoteRecipient[];
  replies: ITeacherNoteReply[];
  /**
   * True only when the caller authored this note and has unseen replies on it.
   * Always false for observer admins who did not author the note.
   *
   * A note CAN appear in both lists — an admin who is also a recipient of a teacher's note sees it
   * here (as an observer) and in their received list. When merging the two, the recipient
   * representation takes precedence because it carries the caller's own seen state.
   */
  hasUnreadActivity: boolean;
  /** One rollup per thread the caller can see — 1:1 notes included. */
  threads: ITeacherNoteThreadSummary[];
  /**
   * Whether the caller has never opened the note itself. Usually false here — an author does not
   * receive their own note — but an admin who is ALSO a recipient gets their real value, mapped
   * from their own recipient row. Merging prefers the received arm for exactly that note, so the
   * two representations never both contribute a count.
   */
  isNoteUnread: boolean;
  /** Unread replies summed across the caller's visible threads. */
  unreadReplyCount: number;
  /** Most recent activity (note creation or latest reply) — the recency sort key. */
  lastActivityTimestamp: Dayjs;
  createdTimestamp: Dayjs;
}

export interface IMyTeacherNote {
  noteId: string;
  text: string;
  attachments: IBlobFileMetadata[];
  /** Optional — a note may be voice-only, text-only, or both. Absent on every note sent before voice notes shipped. */
  voiceNote?: IVoiceNote;
  noteDate: Dayjs;
  auditUserId: string;
  auditUserName: string;
  readTimestamp?: Dayjs;
  /**
   * True when the note went to more than one recipient. The received DTO deliberately exposes no
   * recipient list, so this is the only signal a recipient has that replies could be broadcast.
   */
  isGroupNote: boolean;
  replies: ITeacherNoteReply[];
  /**
   * True when the note has content or replies the caller has not seen yet — the note-level flag,
   * equivalent to `isNoteUnread || unreadReplyCount > 0`.
   */
  hasUnreadActivity: boolean;
  /** One rollup per thread the caller can see — 1:1 notes included. */
  threads: ITeacherNoteThreadSummary[];
  /** Whether the caller has never opened the note itself (independent of its replies). */
  isNoteUnread: boolean;
  /** Unread replies summed across the caller's visible threads. */
  unreadReplyCount: number;
  /** Most recent activity (note creation or latest reply) — the recency sort key. */
  lastActivityTimestamp: Dayjs;
  createdTimestamp: Dayjs;
}

/**
 * Result of marking a note (or one thread of it) seen. Deliberately not a full note — callers patch
 * the returned unread state into their cached copy rather than replacing it.
 */
export interface ITeacherNoteSeenResult {
  noteId: string;
  readTimestamp?: Dayjs;
  isNoteUnread: boolean;
  unreadReplyCount: number;
  threads: ITeacherNoteThreadSummary[];
}

/** Which representation a note was loaded as — participant (received) or author/observer (authored). */
export enum TEACHER_NOTE_SOURCE {
  RECEIVED = 'received',
  AUTHORED = 'authored',
}

/** Fields shared by both representations of a note. */
interface IUnifiedTeacherNoteBase {
  noteId: string;
  text: string;
  attachments: IBlobFileMetadata[];
  /** Optional — a note may be voice-only, text-only, or both. Carried through from both arms. */
  voiceNote?: IVoiceNote;
  noteDate: Dayjs;
  createdTimestamp: Dayjs;
  lastActivityTimestamp: Dayjs;
  hasUnreadActivity: boolean;
  /** One rollup per thread the caller can see — 1:1 notes included. */
  threads: ITeacherNoteThreadSummary[];
  /**
   * Whether the caller has never opened the note itself. Normally false on the authored arm, but a
   * recipient-admin carries a real value there too — the merge prefers the received arm for such a
   * note, so it is only ever counted once.
   */
  isNoteUnread: boolean;
  /** Unread replies summed across the caller's visible threads. */
  unreadReplyCount: number;
  replies: ITeacherNoteReply[];
  authorName: string;
  authorUserId: string;
  isAuthoredByMe: boolean;
  /**
   * True when the note went to more than one recipient — derived from the recipient list on the
   * authored arm, and read from `isGroupNote` on the received arm. Drives whether a reply has an
   * audience to choose between.
   */
  isGroupNote: boolean;
  /** Lowercased text/names/replies, precomputed so per-keystroke search is a cheap scan. */
  searchHaystack: string;
}

/**
 * One note in the unified list, normalised from either representation so the list and detail
 * panes render both directions of the conversation identically. Discriminated on `source`:
 * only the authored/observer arm carries `recipients` (read receipts + per-recipient threads);
 * the received arm is already scoped server-side to the caller's own thread.
 */
export type IUnifiedTeacherNote =
  | (IUnifiedTeacherNoteBase & { source: TEACHER_NOTE_SOURCE.RECEIVED; recipients?: undefined })
  | (IUnifiedTeacherNoteBase & { source: TEACHER_NOTE_SOURCE.AUTHORED; recipients: ITeacherNoteRecipient[] });

export interface ICreateTeacherNoteRequest {
  text: string;
  recipientUserIds: string[];
  attachmentKeys?: string[];
  /** When present, {@link text} stops being required — a note may be voice-only, text-only, or both. */
  voiceNote?: IVoiceNoteRequest;
}

export interface ICreateTeacherNoteReplyRequest {
  noteId: string;
  text: string;
  /**
   * Selects which recipient's private thread to post into. A recipient
   * replying in their own thread omits this (the server infers it from the
   * caller). The note author or an Owner/Admin replying into someone's thread
   * MUST supply it, and it must be a recipient of the note.
   *
   * Ignored when `visibility` is `Everyone` — the server coerces it to null.
   */
  recipientUserId?: string;
  /** Defaults to `Private` server-side when omitted. */
  visibility?: TEACHER_NOTE_REPLY_VISIBILITY;
  /** When present, {@link text} stops being required — a reply may be voice-only, text-only, or both. */
  voiceNote?: IVoiceNoteRequest;
}

/**
 * Marks a note seen. Sent as query params, not a body.
 *
 * Omitting both optional fields marks the note body AND all of the caller's visible threads seen —
 * what opening a note should do. Narrow to one conversation by passing `visibility: Private` with
 * the thread's `recipientUserId`, or `visibility: Everyone` for the shared broadcast thread.
 */
export interface IMarkTeacherNoteAsSeenRequest {
  noteId: string;
  visibility?: TEACHER_NOTE_REPLY_VISIBILITY;
  recipientUserId?: string;
}

export interface IGetTeacherNoteAttachmentDownloadLinkRequest {
  noteId: string;
  attachmentKey: string;
}
